import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { SeoService } from 'src/app/services/seo.service';

type SubmissionState = 'idle' | 'submitting' | 'success' | 'error';

interface ContactPayload {
  name: string;
  email: string;
  phone_number: string;
  organisation: string;
  interest: string;
  message: string;
  privacy_consent: boolean;
  _honey: string;
}

interface ContactApiConfig {
  url: string;
}

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss']
})
export class ContactComponent implements OnInit {
  submissionState: SubmissionState = 'idle';
  submissionError = '';

  private contactEndpoint = '';
  private contactEndpointLoaded = false;

  constructor(
    private readonly seo: SeoService,
    private readonly http: HttpClient
  ) {}

  submitEnquiry(event: Event): void {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const payload: ContactPayload = {
      name: this.value(formData, 'name'),
      email: this.value(formData, 'email'),
      phone_number: this.value(formData, 'phone_number'),
      organisation: this.value(formData, 'organisation'),
      interest: this.value(formData, 'interest'),
      message: this.value(formData, 'message'),
      privacy_consent: this.value(formData, 'privacy_consent') === 'agreed',
      _honey: this.value(formData, '_honey')
    };

    if (payload._honey) {
      // Silently accept bot submissions so the honeypot is not disclosed.
      this.submissionState = 'success';
      form.reset();
      return;
    }

    this.submissionState = 'submitting';
    this.submissionError = '';

    this.ensureContactEndpoint(
      () => this.sendPayload(form, payload),
      (message) => this.handleSubmissionError(message)
    );
  }

  private ensureContactEndpoint(onReady: () => void, onError: (message: string) => void): void {
    if (this.contactEndpointLoaded) {
      if (this.contactEndpoint) {
        onReady();
      } else {
        onError('The contact service has not been configured for this environment.');
      }
      return;
    }

    this.http.get<ContactApiConfig>('/assets/contact-api.json').subscribe({
      next: (config) => {
        this.contactEndpointLoaded = true;
        this.contactEndpoint = String(config?.url || '').trim();

        if (!this.contactEndpoint) {
          onError('The contact service has not been configured for this environment.');
          return;
        }

        onReady();
      },
      error: () => {
        this.contactEndpointLoaded = true;
        onError('The contact service configuration could not be loaded.');
      }
    });
  }

  private sendPayload(form: HTMLFormElement, payload: ContactPayload): void {
    this.http.post<{ ok: boolean; message?: string }>(this.contactEndpoint, payload).subscribe({
      next: (result) => {
        if (!result?.ok) {
          this.handleSubmissionError(result?.message || 'The enquiry could not be submitted.');
          return;
        }

        this.submissionState = 'success';
        this.submissionError = '';
        form.reset();
      },
      error: (error: HttpErrorResponse) => {
        console.error('DotBlack contact enquiry failed:', error);
        const message = error.error?.message || 'Please try again in a moment.';
        this.handleSubmissionError(message);
      }
    });
  }

  private handleSubmissionError(message: string): void {
    this.submissionState = 'error';
    this.submissionError = message;
  }

  private value(formData: FormData, name: string): string {
    return String(formData.get(name) || '').trim();
  }

  ngOnInit(): void {
    this.seo.update({
      title: 'Contact DotBlack | Discuss Your Technology Priorities',
      description: 'Contact DotBlack in Pretoria to discuss IT consulting, automation, project delivery, specialist resources or product demonstrations.',
      path: '/contact/',
      type: 'website',
      schema: { '@context': 'https://schema.org', '@type': 'Organization', name: 'DotBlack Projex Pty (Ltd)', url: 'https://dotblack.co.za/', email: 'mufk@dotblack.co.za', telephone: '+27 11 568 5421' }
    });
  }
}
